import { useState, useEffect, useCallback } from 'react';
import { storageService } from '../services/storage';
import { UserProfile } from '../types';
import { feistyPets } from '../mocks/feistyPets';

// Avatar padrão para novos usuários
const DEFAULT_AVATAR = 'https://public.readdy.ai/ai/img_res/1f32dcfc98a902e26143e90a50138327.jpg';

export const useUserProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const loadProfile = () => {
      let userProfile = storageService.getUserProfile();
      if (!userProfile) {
        userProfile = storageService.createDefaultProfile();
        // Garante que o avatar padrão seja definido
        if (!userProfile.avatar) {
          userProfile.avatar = DEFAULT_AVATAR;
        }
        storageService.saveUserProfile(userProfile);
      } else if (!userProfile.avatar) {
        // Se o perfil existe mas não tem avatar, adiciona o padrão
        userProfile.avatar = DEFAULT_AVATAR;
        storageService.saveUserProfile(userProfile);
      }
      
      // Verifica e atualiza conquistas ao carregar o perfil
      const updatedProfile = checkAndUnlockAchievements(userProfile);
      if (JSON.stringify(updatedProfile) !== JSON.stringify(userProfile)) {
        storageService.saveUserProfile(updatedProfile);
        userProfile = updatedProfile;
      }
      
      setProfile(userProfile);
    };

    loadProfile();
  }, []);

  const checkAndUnlockAchievements = (userProfile: UserProfile): UserProfile => {
    const capturedCount = userProfile.collection.captured.length;
    const totalPets = feistyPets.length;
    const updatedProfile = { ...userProfile };
    let hasChanges = false;

    if (!updatedProfile.achievements) {
      return updatedProfile;
    }

    // Primeira Captura
    if (capturedCount >= 1 && !updatedProfile.achievements[0].unlocked) {
      updatedProfile.achievements[0].unlocked = true;
      updatedProfile.achievements[0].unlockedAt = new Date().toISOString();
      hasChanges = true;
    }

    // Colecionador Iniciante (5 pets)
    if (capturedCount >= 5 && !updatedProfile.achievements[1].unlocked) {
      updatedProfile.achievements[1].unlocked = true;
      updatedProfile.achievements[1].unlockedAt = new Date().toISOString();
      hasChanges = true;
    }

    // Colecionador Dedicado (10 pets)
    if (capturedCount >= 10 && !updatedProfile.achievements[2].unlocked) {
      updatedProfile.achievements[2].unlocked = true;
      updatedProfile.achievements[2].unlockedAt = new Date().toISOString();
      hasChanges = true;
    }

    // Colecionador Expert (25 pets)
    if (capturedCount >= 25 && !updatedProfile.achievements[3].unlocked) {
      updatedProfile.achievements[3].unlocked = true;
      updatedProfile.achievements[3].unlockedAt = new Date().toISOString();
      hasChanges = true;
    }

    // Mestre Colecionador (coleção completa)
    if (capturedCount >= totalPets && !updatedProfile.achievements[4].unlocked) {
      updatedProfile.achievements[4].unlocked = true;
      updatedProfile.achievements[4].unlockedAt = new Date().toISOString();
      hasChanges = true;
    }

    return updatedProfile;
  };

  const refreshProfile = () => {
    const userProfile = storageService.getUserProfile();
    setProfile(userProfile);
  };

  const toggleCaptured = (petId: string) => {
    if (!profile) return;

    const previousCount = profile.collection.captured.length;
    const updatedProfile = { ...profile };
    const capturedIndex = updatedProfile.collection.captured.indexOf(petId);
    
    if (capturedIndex > -1) {
      // Remove da coleção
      updatedProfile.collection.captured.splice(capturedIndex, 1);
    } else {
      // Adiciona à coleção
      updatedProfile.collection.captured.push(petId);
      // Remove da wishlist se estiver lá
      const wishlistIndex = updatedProfile.collection.wishlist.indexOf(petId);
      if (wishlistIndex > -1) {
        updatedProfile.collection.wishlist.splice(wishlistIndex, 1);
      }
    }
    
    const currentCount = updatedProfile.collection.captured.length;
    
    // Verifica e desbloqueia conquistas
    const profileWithAchievements = checkAndUnlockAchievements(updatedProfile);
    
    storageService.saveUserProfile(profileWithAchievements);
    setProfile(profileWithAchievements);
    
    // Dispara o evento de verificação de conquistas
    setTimeout(() => {
      const event = new CustomEvent('achievementCheck', {
        detail: { previousCount, currentCount }
      });
      window.dispatchEvent(event);
    }, 100);
  };

  const toggleWishlist = (petId: string) => {
    if (!profile) return;

    const updatedProfile = { ...profile };
    const wishlistIndex = updatedProfile.collection.wishlist.indexOf(petId);
    
    if (wishlistIndex > -1) {
      // Remove da wishlist
      updatedProfile.collection.wishlist.splice(wishlistIndex, 1);
    } else {
      // Adiciona à wishlist
      updatedProfile.collection.wishlist.push(petId);
      // Remove da coleção se estiver lá
      const capturedIndex = updatedProfile.collection.captured.indexOf(petId);
      if (capturedIndex > -1) {
        updatedProfile.collection.captured.splice(capturedIndex, 1);
      }
    }
    
    storageService.saveUserProfile(updatedProfile);
    setProfile(updatedProfile);
  };

  const updateProfile = (updates: { username?: string; avatar?: string }) => {
    if (!profile) return;

    const updatedProfile = {
      ...profile,
      ...(updates.username && { username: updates.username }),
      ...(updates.avatar && { avatar: updates.avatar })
    };

    storageService.saveUserProfile(updatedProfile);
    setProfile(updatedProfile);
  };

  const exportCollection = () => {
    const data = storageService.exportCollection();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'feisty-pets-collection.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCollection = (data: UserProfile) => {
    storageService.saveUserProfile(data);
    refreshProfile();
  };

  const addToCollection = useCallback((petId: string) => {
    setProfile(prev => {
      if (!prev) return prev;
      
      const petIdStr = String(petId);
      
      // Verifica se já está na coleção
      if (prev.collection.captured.includes(petIdStr)) {
        return prev;
      }

      const previousCount = prev.collection.captured.length;
      const newCaptured = [...prev.collection.captured, petIdStr];
      const currentCount = newCaptured.length;

      const updatedProfile = {
        ...prev,
        collection: {
          ...prev.collection,
          captured: newCaptured
        }
      };

      storageService.saveUserProfile(updatedProfile);

      // Dispara evento de verificação de conquistas APENAS UMA VEZ
      setTimeout(() => {
        const event = new CustomEvent('achievementCheck', {
          detail: { previousCount, currentCount }
        });
        window.dispatchEvent(event);
      }, 100);

      return updatedProfile;
    });
  }, []);

  return { 
    userProfile: profile, 
    refreshProfile,
    toggleCaptured,
    toggleWishlist,
    updateProfile,
    exportCollection,
    importCollection,
    addToCollection
  };
};